<template>
  <AppLoading v-if="isLoading" class="mt-0" />

  <div v-else>
    <h3 class="font-weight-medium" data-test="appHeading">
      Editing {{ $localize('department') | capitalize }} {{ department.displayName }}
    </h3>
    <div class="form-group">
      <label class="heading smaller-heading">{{ $localize('department') | capitalize }} Display Name</label>
      <input
        v-model="department.displayName"
        class="form-control"
        placeholder="Display name"
        :disabled="!editable"
        required
        @input="update"
      />
    </div>
    <div class="form-group">
      <label class="heading smaller-heading">{{ $localize('department') | capitalize }} Full Name</label>
      <input
        v-model="department.name"
        class="form-control"
        placeholder="Name"
        required
        :disabled="!editable"
        @input="update"
      />
    </div>
    <div class="form-group">
      <label class="heading smaller-heading">Subject codes</label>
      <Multiselect
        v-model="department.subjectCodes"
        data-test="subjectCodes"
        :disabled="!editable"
        :options="codeOptions"
        placeholder="Add a Subject Code"
        :taggable="true"
        :multiple="true"
        tag-placeholder="Add this as new code"
        @tag="addTag"
        @input="update"
      />
    </div>

    <div class="form-group">
      <label class="heading smaller-heading">Status</label>
      <Multiselect
        v-model="department.status"
        :options="['Active', 'Inactive']"
        placeholder="Set Status"
        :disabled="!editable"
        @input="update"
      />
    </div>

    <!-- Catalog Fields - only visible by coursedog users for now -->
    <div v-if="department && isCoursedogAdmin" class="mb-4">
      <SchemaForm
        :value="department"
        :fields="departmentCatalogFields"
        :disabled="false"
        @input="updateDepartmentCatalogFields"
      />
    </div>
    <div class="my-2 smaller-heading heading">{{ $localize('department') | capitalize }} Chairs</div>
    <DepartmentChairEditor
      :editable="editable"
      class="rounded border"
      :chairs="department.chair"
      @update="updateChairs"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Multiselect from '@/components/Multiselect'
import DepartmentChairEditor from '@/components/DepartmentChairEditor'
import SchemaForm from '@/components/Forms/Form'
import FormText from '@/components/Forms/FormText'
import FormTextarea from '@/components/Forms/FormTextarea'
import FormUploadImage from '@/components/Forms/FormUploadImage'
import {
  uploadToS3
} from '@/api'
import { capitalize } from '@shared/utils/string'
export default {
  components: {
    Multiselect,
    DepartmentChairEditor,
    SchemaForm,
  },
  data () {
    return {
      department: {},
      codeOptions: [],
      isLoading: true,
    }
  },
  computed: {
    ...mapGetters('departments', [ 'departmentById' ]),
    ...mapGetters([ 'userRoles', 'school' ]),
    editable () {
      return this.$hasAccess().to('cmSettings').to('editDepartments').value()
    },
    currentDepartment () {
      return this.departmentById(this.$route.params.departmentId)
    },
    isCoursedogAdmin () {
      return this.userRoles.includes('coursedog')
    },
    departmentCatalogFields () {
      return [
        {
          name: 'catalogDisplayName',
          label: `${capitalize(this.$localize('catalog'))} Display Name`,
          placeholder: `Set display name for ${this.$localize('catalog')}`,
          type: FormText,
        },
        {
          name: 'catalogDescription',
          label: `${capitalize(this.$localize('catalog'))} Description`,
          placeholder: `Set description for ${this.$localize('catalog')}`,
          type: FormTextarea,
        },
        {
          name: 'catalogImageUrl',
          label: `${capitalize(this.$localize('catalog'))} Image`,
          placeholder: `Set Image for ${this.$localize('catalog')}`,
          type: FormUploadImage,
          uploadProvider: this.uploadProvider,
        },
      ]
    },
  },
  watch: {
    currentDepartment: {
      immediate: true,
      handler () {
        if (this.currentDepartment) {
          this.isLoading = false
          this.department = this.currentDepartment
        }
      },
    },
  },
  methods: {
    addTag (tag) {
      this.codeOptions.push(tag)
      if (Array.isArray(this.department.subjectCodes)) {
        this.department.subjectCodes.push(tag)
      } else this.$set(this.department, 'subjectCodes', [ tag ])
      this.update()
    },
    updateChairs (chairs) {
      this.$set(this.department, 'chair', chairs)
      this.$store.dispatch('departments/setDepartmentAttribute', {
        key: 'chair',
        value: this.department.chair,
        departmentId: this.department.id,
      })
    },
    async uploadProvider (image) {
      try {
        const imageUrl = await uploadToS3({
          school: this.school,
          file: image,
          bucketName: 'coursedog-images-public',
        })
        return imageUrl.split('?')[0]
      } catch (err) {
        alert('Something went wrong, please try another file')
        console.error(err)
        return null
      }
    },
    updateDepartmentCatalogFields (changes = {}) {
      this.department = {
        ...this.department,
        ...changes,
      }
      this.update()
    },
    update () {
      return this.$store.dispatch('departments/setDepartment', { department: this.department })
    },
  },
}
</script>
